import Stripe from 'stripe';

let stripe: Stripe | null = null;

export type PlanType = 'free' | 'plus' | 'pro' | 'max';
export type BillingCycle = 'monthly' | 'yearly' | null;

export interface PlanInfo {
  planType: PlanType;
  billingCycle: BillingCycle;
  displayName: string;
}

export function getStripe() {
  if (!process.env.STRIPE_SECRET_KEY) {
    throw new Error('Missing STRIPE_SECRET_KEY environment variable');
  }
  if (!stripe) {
    stripe = new Stripe(process.env.STRIPE_SECRET_KEY as string, {
      // apiVersion: '2025-10-29.clover',
      typescript: true,
    });
  }
  return stripe;
}

export const getStripePublicKey = () => {
  return process.env.STRIPE_PUBLIC_KEY || '';
};

export type LocalSubscriptionStatus =
  | 'active'
  | 'canceled'
  | 'expired'
  | 'past_due'
  | 'trialing'
  | 'incomplete';

/**
 * Stripe 订阅状态 -> 本地 subscriptions.status 枚举，取语义最接近的一档。
 *
 * 已付但关闭自动续费（cancel_at_period_end / cancel_at）保持 active / trialing，由
 * cancelAtPeriodEnd 列表达，权益保留到 currentPeriodEnd；`incomplete` 只表示真正未完成支付，
 * 不授予付费权益（SPEC §4.2）。cancellation 参数保留以兼容调用方，不再改变状态。
 */
export function mapStripeStatusToLocal(
  status: Stripe.Subscription.Status,
  _cancellation?: { cancelAtPeriodEnd?: boolean | null; cancelAt?: number | null },
): LocalSubscriptionStatus {
  switch (status) {
    case 'active':
      return 'active';
    case 'trialing':
      return 'trialing';
    case 'past_due':
    case 'unpaid':
    case 'paused':
      return 'past_due';
    case 'canceled':
      return 'canceled';
    case 'incomplete_expired':
      return 'expired';
    case 'incomplete':
    default:
      return 'incomplete';
  }
}
/**
 * 升级切换（applyUpgradeSwitch）用 trial_end 推迟下一次扣款，差价已另行收取，Stripe 显示 trialing
 * 但这段是已付周期：本地记为 active，不能被展示成新客试用，也不能消耗试用资格。
 */
export function localStatusForStripeSubscription(
  subscription: Pick<Stripe.Subscription, 'status' | 'metadata'>,
): LocalSubscriptionStatus {
  if (
    subscription.status === 'trialing' &&
    subscription.metadata?.upgrade_type === 'subscription_upgrade'
  ) {
    return 'active';
  }
  return mapStripeStatusToLocal(subscription.status);
}

const isTestEnv =
  process.env.NEXT_PUBLIC_TEST_ENV === 'true' ||
  (typeof window !== 'undefined' && window.location?.origin === 'https://test.alfagpt.org');

export const STRIPE_PRICE_PACKAGES = isTestEnv
  ? {
      small: 'price_1SM2QtFGeUECnR82kdQR5uHJ',
      big: 'price_1SM2ROFGeUECnR82bvuEiteK',
    }
  : {
      small: 'price_1SVS79F0SG78Enih9FBKM9UA',
      big: 'price_1SVS79F0SG78Enih7IQ9ByFK',
    };

/**
 * Forge / Pythra 在售的四个 Stripe 价格（Pro / Max × 月 / 年，SPEC §4.1）。实际 price ID 按
 * test / live 环境分别配置，不跨环境混用。
 */
export const forgePriceKeys = [
  'STRIPE_PRICE_FORGE_PRO_MONTHLY',
  'STRIPE_PRICE_FORGE_PRO_YEARLY',
  'STRIPE_PRICE_FORGE_MAX_MONTHLY',
  'STRIPE_PRICE_FORGE_MAX_YEARLY',
] as const;

/**
 * 旧 App 读取的 FORGE_PLUS_* 槽位只作兼容别名：返回值始终等于新 Pro 价格，不再售卖旧 Plus 价格。
 * 新 PRO 变量缺失时不得回退旧 PLUS：停售价格不能因为部署漏配重新开放购买。
 */
const LEGACY_FORGE_PRICE_ALIASES = {
  STRIPE_PRICE_FORGE_PLUS_MONTHLY: 'STRIPE_PRICE_FORGE_PRO_MONTHLY',
  STRIPE_PRICE_FORGE_PLUS_YEARLY: 'STRIPE_PRICE_FORGE_PRO_YEARLY',
} as const;

function forgePriceEnv(key: (typeof forgePriceKeys)[number]): string {
  return process.env[key]?.trim() || '';
}

export function readForgePriceSettings(): Record<string, string> {
  const settings: Record<string, string> = Object.fromEntries(
    forgePriceKeys.map((key) => [key, forgePriceEnv(key)]),
  );
  for (const [legacyKey, key] of Object.entries(LEGACY_FORGE_PRICE_ALIASES)) {
    settings[legacyKey] = settings[key];
  }
  return settings;
}

/** 当前允许新购的 Forge 价格；已停售的旧 Plus / Max 价格不在其中，但历史续订照常识别。 */
export function getForgeSellablePriceIds(): Set<string> {
  const values = forgePriceKeys.map(forgePriceEnv);
  return values.every(Boolean) && new Set(values).size === forgePriceKeys.length
    ? new Set(values)
    : new Set();
}

export type ForgePriceValidation =
  | { valid: true }
  | { valid: false; code: 'BILLING_UNAVAILABLE' | 'PRICE_NOT_AVAILABLE'; status: 400 | 503 };

/** New purchases only. Historical receipt/renewal parsing must not use this allowlist. */
export function validateForgeSellablePrice(price: Stripe.Price): ForgePriceValidation {
  const ids = getForgeSellablePriceIds();
  if (ids.size !== 4) return { valid: false, code: 'BILLING_UNAVAILABLE', status: 503 };
  if (!ids.has(price.id)) return { valid: false, code: 'PRICE_NOT_AVAILABLE', status: 400 };
  const index = forgePriceKeys.findIndex((key) => forgePriceEnv(key) === price.id);
  const expected = [
    { plan: 'pro', interval: 'month', amount: 1999 },
    { plan: 'pro', interval: 'year', amount: 19900 },
    { plan: 'max', interval: 'month', amount: 19900 },
    { plan: 'max', interval: 'year', amount: 199000 },
  ][index];
  const key = process.env.STRIPE_SECRET_KEY;
  const liveMode = key?.startsWith('sk_live_') || key?.startsWith('rk_live_');
  const hasKnownMode = liveMode || key?.startsWith('sk_test_') || key?.startsWith('rk_test_');
  if (
    !expected ||
    !price.active ||
    price.type !== 'recurring' ||
    price.currency !== 'usd' ||
    price.unit_amount !== expected.amount ||
    price.recurring?.interval !== expected.interval ||
    price.recurring.interval_count !== 1 ||
    price.metadata?.source !== 'forge' ||
    price.metadata?.membership_level !== expected.plan ||
    !hasKnownMode ||
    price.livemode !== Boolean(liveMode)
  )
    return { valid: false, code: 'BILLING_UNAVAILABLE', status: 503 };
  return { valid: true };
}

export const searchPriceKeys = [
  'STRIPE_PRICE_PRO_MONTHLY',
  'STRIPE_PRICE_PRO_YEARLY',
  'STRIPE_PRICE_MAX_MONTHLY',
  'STRIPE_PRICE_MAX_YEARLY',
  'STRIPE_PRICE_DATA_PLUS_BIG',
  'STRIPE_PRICE_DATA_PLUS_SMALL',
  'STRIPE_PRICE_PLUS_MONTHLY',
  'STRIPE_PRICE_PLUS_YEARLY',
] as const;

type PriceSettingsGroup = 'forge' | 'search';

const PRICE_KEYS_BY_GROUP: Record<PriceSettingsGroup, readonly string[]> = {
  forge: forgePriceKeys,
  search: searchPriceKeys,
};

export function readEnvGroup(keys: readonly string[]) {
  return Object.fromEntries(keys.map((key) => [key, process.env[key] || '']));
}

export function resolvePriceIdsForGroup(group: string): string[] {
  if (group === 'forge') return [...getForgeSellablePriceIds()];
  const keys = PRICE_KEYS_BY_GROUP[group as PriceSettingsGroup];
  if (!keys) {
    return [];
  }
  return keys.map((key) => process.env[key] || '').filter(Boolean);
}

/**
 * 根据 planId 和 billingCycle 获取数据库中的 membership_status 值
 * @param planId - 套餐ID (plus、pro 或 max)
 * @param billingCycle - 计费周期 (monthly 或 yearly)
 * @returns membership_status 值 (monthly_plus, yearly_plus, monthly, yearly, monthly_max, yearly_max)
 */
export function getPlanType(planId: string, billingCycle: 'monthly' | 'yearly'): string {
  if (planId === 'plus') {
    return billingCycle === 'yearly' ? 'yearly_plus' : 'monthly_plus';
  } else if (planId === 'pro') {
    return billingCycle === 'yearly' ? 'yearly_pro' : 'monthly_pro';
  } else if (planId === 'max') {
    return billingCycle === 'yearly' ? 'yearly_max' : 'monthly_max';
  }

  // 默认返回 monthly（不应该发生，因为 free 用户不会调用此函数）
  return 'monthly_pro';
}

/**
 * 根据数据库的 membership_status 值获取会员等级信息
 * @param membershipStatus - 数据库中的会员状态值
 * @returns 会员等级信息
 */
export function getPlanInfo(membershipStatus: string): PlanInfo {
  switch (membershipStatus) {
    case 'free':
      return {
        planType: 'free',
        billingCycle: null,
        displayName: 'Free',
      };
    case 'monthly_plus':
      return {
        planType: 'plus',
        billingCycle: 'monthly',
        displayName: 'Plus Monthly',
      };
    case 'yearly_plus':
      return {
        planType: 'plus',
        billingCycle: 'yearly',
        displayName: 'Plus Annual',
      };
    case 'monthly_pro':
      return {
        planType: 'pro',
        billingCycle: 'monthly',
        displayName: 'Pro Monthly',
      };
    case 'yearly_pro':
      return {
        planType: 'pro',
        billingCycle: 'yearly',
        displayName: 'Pro Annual',
      };
    case 'monthly_max':
      return {
        planType: 'max',
        billingCycle: 'monthly',
        displayName: 'Max Monthly',
      };
    case 'yearly_max':
      return {
        planType: 'max',
        billingCycle: 'yearly',
        displayName: 'Max Annual',
      };
    default:
      // 默认返回免费用户
      return {
        planType: 'free',
        billingCycle: null,
        displayName: 'Free',
      };
  }
}
